import { useEffect, useRef, useState } from 'react';
import { usePrizePool, useTokenSelector } from '../../../hooks';

import './current-prize-pool.css';

import { TrendingUp, Crown, Users } from 'lucide-react';
import { Spin } from '../../spin';

export function CurrentPrizePool() {
  const [showFixed, setShowFixed] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);

  const minLoot: number = Number(
    process.env.REACT_APP_MIN_CURRENT_LOOT_DEFAULT,
  );

  const prizePool = usePrizePool();
  const { getPrizeDistribution, isLoadingTokens } = useTokenSelector();

  const distribution = getPrizeDistribution(prizePool ?? {});

  // 🔥 BASE GLOBAL CORRECTA
  const baseCurrentLoot =
    (prizePool.currentLoot ?? 0) < minLoot
      ? minLoot
      : (prizePool.currentLoot ?? 0);

  const getColor = (position: number) => {
    if (position === 1) return '#FFD700';
    if (position === 2) return '#C0C0C0';
    if (position === 3) return '#CD7F32';
    return '#90F7FF';
  };

  const getVariant = (position: number) => {
    if (position === 1) return 'first';
    if (position === 2) return 'second';
    if (position === 3) return 'third';
    return 'default';
  };

  useEffect(() => {
    const handleScroll = () => {
      if (mainRef.current) {
        const rect = mainRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        setShowFixed(!isVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div ref={mainRef} className="current-prizepool">
        <div className="current-prizepool-header">
          <TrendingUp
            className="current-prizepool-icon-main"
            stroke="#CAFB10"
          />

          <div>
            <h2 className="current-prizepool-title">
              Botín de la Temporada Actual
            </h2>
            <p className="current-prizepool-subtitle">
              Premios acumulados para los ganadores de esta temporada
            </p>
          </div>
        </div>

        <div className="current-prizepool-grid">
          {distribution.map((item) => {
            const maxAmount =
              prizePool[item.key as keyof typeof prizePool] ?? 0;

            if (!maxAmount) return null;

            const percentageFactor =
              prizePool.maxLoot > 0 ? maxAmount / prizePool.maxLoot : 0;

            const currentAmount = baseCurrentLoot * percentageFactor;

            const percentage = (currentAmount / maxAmount) * 100;

            const color = getColor(item.position);
            const variant = getVariant(item.position);

            return (
              <div
                key={item.key}
                className={`current-prizepool-card ${variant}`}
              >
                <div className="current-prizepool-card-header">
                  {item.isReferrer ? (
                    <Users
                      className="current-prizepool-icon referrer"
                      stroke={color}
                    />
                  ) : (
                    <Crown className="current-prizepool-icon" stroke={color} />
                  )}

                  <div className="current-prizepool-position">
                    {item.position}° {item.isReferrer ? 'Referidor' : 'Ganador'}
                  </div>
                </div>

                <div className="current-prizepool-amounts">
                  <div className="current-prizepool-row">
                    <span className="label">Actual:</span>
                    <span className="value main">
                      {isLoadingTokens ? (
                        <Spin />
                      ) : (
                        <>
                          €
                          {currentAmount.toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </>
                      )}
                    </span>
                  </div>

                  <div className="current-prizepool-row">
                    <span className="label small">Máximo:</span>
                    <span className="value small">
                      {isLoadingTokens ? (
                        <Spin />
                      ) : (
                        <>€{maxAmount.toLocaleString('es-ES')}</>
                      )}
                    </span>
                  </div>
                </div>

                <div className="current-prizepool-progress">
                  <div className="track">
                    <div
                      className={`fill ${variant}`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>

                  <div className="percent">
                    {isLoadingTokens ? <Spin /> : percentage.toFixed(1)}% del máximo
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="current-prizepool-info">
          <TrendingUp className="info-icon" stroke="#90F7FF" />

          <p>
            Los premios aumentan con cada token canjeado. El referidor recibe un
            porcentaje del premio del ganador que refirió.
          </p>
        </div>
      </div>

      {/* 🔥 FIXED / COMPACT CORREGIDO */}
      <div
        className={`current-prize-pool-fixed ${
          showFixed ? 'current-prize-pool-fixed-visible' : ''
        }`}
      >
        <div className="current-prize-pool-fixed-content">
          {distribution.map((item) => {
            const maxAmount =
              prizePool[item.key as keyof typeof prizePool] ?? 0;

            if (!maxAmount) return null;

            const percentageFactor =
              prizePool.maxLoot > 0 ? maxAmount / prizePool.maxLoot : 0;

            const currentAmount = baseCurrentLoot * percentageFactor;

            const color = getColor(item.position);

            const Icon = item.isReferrer ? Users : Crown;

            return (
              <div
                key={item.key}
                className="current-prize-pool-fixed-item"
                style={{ color }}
              >
                <Icon
                  className="current-prize-pool-fixed-icon"
                  stroke={color}
                />

                <span className="current-prize-pool-fixed-position">
                  {item.position}
                </span>

                <span className="current-prize-pool-fixed-amount">
                  €
                  {currentAmount.toLocaleString('es-ES', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
